<template>
    <div>
        <div class="card shadow mb-5">
            <div class="card-header">Synthèse hebdomadaire</div>

            <b-card-body>
                <div v-if="!hasErrors" class="row mb-3">
                    <div class="col-3">
                        <accounting-firm-user-select
                            @collaborator-has-changed="getItems"
                        />
                    </div>
                    <div class="col-3 pt-1">
                        <b-button-group size="sm" class="btn-block">
                            <b-button
                                :pressed="reportUnit === 'hours'"
                                variant="outline-secondary"
                                @click="setReportUnit('hours')"
                            >
                                En heures
                            </b-button>
                            <b-button
                                :pressed="reportUnit === 'euros'"
                                variant="outline-secondary"
                                @click="setReportUnit('euros')"
                            >
                                En euros
                            </b-button>
                        </b-button-group>
                    </div>
                    <div class="col-6 text-right pt-1">
                        <week-handler
                            :start="meta.start"
                            :end="meta.end"
                            :next="meta.next"
                            :prev="meta.prev"
                            @week-has-changed="getItems"
                        />
                    </div>
                </div>
                <div v-if="loading">
                    <loading></loading>
                </div>

                <div v-else>
                    <b-alert variant="danger" :show="hasErrors">
                        <span v-html="errorMessage"></span>
                    </b-alert>

                    <b-table
                        responsive
                        small
                        :items="items"
                        :fields="fields"
                        :filter="filter"
                        :per-page="perPage"
                        :current-page="currentPage"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        primary-key="id"
                        @filtered="onFiltered"
                    >
                        <template #cell(d1)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d1"
                            />
                        </template>
                        <template #cell(d2)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d2"
                            />
                        </template>
                        <template #cell(d3)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d3"
                            />
                        </template>
                        <template #cell(d4)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d4"
                            />
                        </template>
                        <template #cell(d5)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d5"
                            />
                        </template>
                        <template #cell(d6)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.d6"
                            />
                        </template>
                        <template #cell(sum)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.sum"
                            />
                        </template>
                        <template #cell(contractual_week_hours)="data">
                            <b-progress
                                :value="data.item.sum.duration"
                                :max="meta.contractual_week_hours"
                                variant="secondary"
                                height="12px"
                                class="mt-1"
                            />
                        </template>

                        <template slot="bottom-row">
                            <td class="td-bold">TOTAL</td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD1"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD2"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD3"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD4"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD5"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalD6"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalSum"
                                />
                            </td>
                            <td>
                                <b-progress
                                    :value="totalSum.duration"
                                    :max="meta.contractual_week_hours"
                                    :variant="totalChargeVariant"
                                    height="12px"
                                    class="mt-1"
                                />
                            </td>
                        </template>
                    </b-table>

                    <!-- Pagination -->
                    <b-row>
                        <b-col md="3" class="my-1 text-center">
                            <b-pagination
                                v-if="totalRows > 0"
                                v-model="currentPage"
                                :total-rows="totalRows"
                                :per-page="perPage"
                                class="my-0"
                                size="sm"
                            />
                        </b-col>
                        <b-col md="6" class="my-2">
                            <p class="text-center small pt-0">
                                {{ legend }}
                            </p>
                        </b-col>
                        <b-col md="3" class="my-1 text-right">
                            <b-dropdown
                                right
                                split
                                size="sm"
                                :text="perPage + ' éléments par page'"
                            >
                                <b-dropdown-item
                                    v-for="pageOption in pageOptions"
                                    :key="pageOption"
                                    @click="setPerPage(pageOption)"
                                >
                                    {{ pageOption }}
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                    <!-- /Pagination -->
                </div>
            </b-card-body>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import TableHandler from '../../../common/mixins/TableHandler.vue'
import MonthChargeHandler from '../../../common/components/MonthChargeHandler.vue'
import AccountingFirmUserSelect from './SelectAccountingFirmUser.vue'
import loading from '../../../common/components/loading.vue'
import weekHandler from './WeekHandler.vue'
import DashboardHandler from '../../../dashboard/mixins/DashboardHandler.vue'

export default {
    name: 'CollaboratorsWeekReport',

    components: {
        AccountingFirmUserSelect,
        MonthChargeHandler,
        loading,
        weekHandler,
    },

    mixins: [TableHandler, DashboardHandler],

    props: {
        user: {
            required: true,
            type: Number,
        },
        todayY: {
            required: true,
            type: Number,
        },
        todayM: {
            required: true,
            type: Number,
        },
        todayD: {
            required: true,
            type: Number,
        },
    },

    data() {
        return {
            reportUnit: 'hours',
            meta: {},
            fields: [
                {
                    key: 'client',
                    label: 'Affectation',
                    sortable: true,
                },
                {
                    key: 'd1',
                    label: 'lun',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'd2',
                    label: 'mar',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'd3',
                    label: 'mer',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'd4',
                    label: 'jeu',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'd5',
                    label: 'ven',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'd6',
                    label: 'sam',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'sum',
                    label: 'TOTAL',
                    sortable: true,
                    class: 'text-right td-bold',
                },
                {
                    key: 'contractual_week_hours',
                    label: '',
                    class: 'col-uniform',
                },
            ],
        }
    },

    computed: {
        ...mapState({
            accountingFirmUser: (state) =>
                state.timeSheetsVM.filters.accountingFirmUser,
            currentDate: (state) => state.timeSheetsVM.currentDate,
        }),

        totalD1() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d1.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d1.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalD2() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d2.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d2.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalD3() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d3.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d3.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalD4() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d4.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d4.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalD5() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d5.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d5.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalD6() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d6.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.d6.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalSum() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalChargeVariant() {
            const charge =
                (100 * this.totalSum.duration) /
                this.meta.contractual_week_hours
            if (charge > 100) {
                return 'primary'
            }
            if (charge > this.meta.success) {
                return 'success'
            }
            if (charge > this.meta.warning) {
                return 'warning'
            }
            return 'danger'
        },
    },

    async created() {
        this.$store.commit(
            'browsing/SET_PAGE_NAME',
            'accountingFirmUserTimeSheetWeekReport'
        )
        this.$store.commit(
            'timeSheetsVM/SET_CURRENT_DATE',
            `${this.todayY}/${this.todayM}/${this.todayD}`
        )
        this.$store.commit(
            'timeSheetsVM/SET_ACCOUNTING_FIRM_USER_OPTION',
            this.user
        )
        await this.getItems()
    },

    methods: {
        async getItems() {
            try {
                this.loading = true
                const response = await this.$axios.get(
                    `/time-sheets/reporting/accountingFirmUsers/${this.accountingFirmUser}/week/${this.currentDate}`
                )
                this.items = response.data.data
                this.meta = response.data.meta
                this.loading = false
                this.updateTotalRows()
            } catch (error) {
                // console.log(error)
                this.handleErrors(error.response)
            }
        },

        setReportUnit(units) {
            this.reportUnit = units
        },
    },
}
</script>

<style scoped></style>
